// The *Panel acknowledgment summary* Home widget (B10 slice S21; plan D15; CR-086 "Panel acknowledgment summary": "acknowledgments,
// declines and nonresponse per interviewer visible to the Coordinator (Coordinator Workbench)"; DC-162 "Panel acknowledgment
// summary as rows; a decline opens the Panel change."). One D0 AckSummary per requisition — the ring (accepted n of N),
// the three counts *Accepted* · *Acknowledgment Pending* · *Declined* (a nonresponse is still pending) and one row per
// interviewer — then the link to the Coordinator Workbench, where the same acknowledgments are rows.
//
// Registered as Home widget `panel_ack` (./register.ts). The data is B6's (`panelgov/summary.py` through the `panel_ack`
// provider in app/reporting/panel_ack.py, PLAN §0.3 N5): with no released panel to show, Home never lists the widget
// and this view is never drawn ("A widget whose data no lane builds yet … is ABSENT, never a placeholder"); with data
// but no row it draws nothing.
// Only the allow-listed fields below are read: whatever else the data carries is never drawn. Every route is the
// provider's, given only where the reader may follow it (server-side, deny by default); this view never builds one, so
// an absent route is an absent link or button (DC-24). Pure of the router at load (it runs under the server renderer in
// the tests); the Panel change button follows its route through the router only when pressed.
//
// Frozen data contract (S21 → B6's provider; DashboardConfig key `panel_ack`):
//   { summaries: [{ requisition_id, title?, route?, rows: [{ key, name, round, assigned?: [word], status:
//     "accepted" | "declined" | "pending" | "nonresponse", nonresponse?: bool, responded_at?: ISO, panel_change_route? }] }],
//     workbench_route? }

import { ACKSUM_LABELS, AckSummary, NAV_ITEM_LABELS, Stack, TextLink, Widget, type AckSummaryRow, type ResponseStatus } from "@/mc";
import type { HomeWidgetView } from "../home/host";
import { hsKey } from "@/views/b10/lib/hsKey";
import { WORKBENCH_LABELS } from "./labels";

/** One interviewer's acknowledgment as the widget data carries it. */
export interface AckRowData {
  key: string;
  name: string;
  round: string;
  assigned?: readonly string[] | undefined;
  status: string;
  /** A pending acknowledgment past the Admin threshold. */
  nonresponse?: boolean | undefined;
  responded_at?: string | null | undefined;
  /** Where a Declined row's *Panel change* opens, when the reader may open it. */
  panel_change_route?: string | null | undefined;
}

/** One requisition's acknowledgments. */
export interface AckSummaryData {
  requisition_id: string;
  title?: string | null | undefined;
  /** The requisition's Kickoff & Packets, when the reader may open it. */
  route?: string | null | undefined;
  rows: readonly AckRowData[];
}

/** The `panel_ack` widget data. */
export interface PanelAckData {
  summaries: readonly AckSummaryData[];
  /** The Coordinator Workbench, when the reader holds it. */
  workbench_route?: string | null | undefined;
}

const STATUSES: readonly ResponseStatus[] = ["accepted", "declined", "pending", "nonresponse"];

/** An app route: a hash path the shell resolves (never an outside address). */
export const isAppRoute = (value: unknown): value is string => typeof value === "string" && /^#[a-z]/.test(value);

const text = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value : null);

/** The response a row shows: his four words; a pending row past the threshold is a nonresponse; anything else is not drawn. */
export function responseOf(row: { status?: unknown; nonresponse?: unknown }): ResponseStatus | null {
  const status = row.status;
  if (typeof status !== "string" || !(STATUSES as readonly string[]).includes(status)) return null;
  return status === "pending" && row.nonresponse === true ? "nonresponse" : (status as ResponseStatus);
}

/** The data narrowed to the fields this widget draws: rows with a known response, summaries with at least one row. */
export function ackSummaries(data: Record<string, unknown> | null | undefined): AckSummaryData[] {
  const raw = data?.["summaries"];
  if (!Array.isArray(raw)) return [];
  const out: AckSummaryData[] = [];
  for (const item of raw as unknown[]) {
    if (!item || typeof item !== "object") continue;
    const summary = item as Record<string, unknown>;
    const requisitionId = text(summary["requisition_id"]);
    const rows = Array.isArray(summary["rows"]) ? (summary["rows"] as unknown[]) : [];
    if (!requisitionId) continue;
    const kept: AckRowData[] = [];
    for (const entry of rows) {
      if (!entry || typeof entry !== "object") continue;
      const row = entry as Record<string, unknown>;
      const status = responseOf(row);
      const key = text(row["key"]);
      const name = text(row["name"]);
      if (!status || !key || !name) continue;
      const assigned = row["assigned"];
      const panelChange = row["panel_change_route"];
      kept.push({
        key,
        name,
        round: text(row["round"]) ?? "",
        assigned: Array.isArray(assigned) ? (assigned as unknown[]).filter((word): word is string => typeof word === "string") : [],
        status,
        responded_at: text(row["responded_at"]),
        panel_change_route: isAppRoute(panelChange) ? panelChange : null,
      });
    }
    if (kept.length === 0) continue;
    const route = summary["route"];
    out.push({ requisition_id: requisitionId, title: text(summary["title"]), route: isAppRoute(route) ? route : null, rows: kept });
  }
  return out;
}

/** Follows a route through the shell's router (loaded on press, so this module stays renderable on the server). */
function follow(route: string): void {
  void import("@/router").then(({ go }) => go(route));
}

/** The AckSummary rows of one requisition: the Panel change only on a Declined row whose route the provider gave. */
export function ackRows(summary: AckSummaryData): AckSummaryRow[] {
  return summary.rows.map((row) => {
    const route = row.panel_change_route;
    return {
      key: row.key,
      name: row.name,
      round: row.round,
      assigned: row.assigned ?? [],
      status: row.status as ResponseStatus,
      respondedAt: row.responded_at ?? undefined,
      canPanelChange: isAppRoute(route),
      onPanelChange: isAppRoute(route) ? () => follow(route) : undefined,
      hs: hsKey("dash.ack.panel-change", row.key),
    };
  });
}

/** The heading of one requisition's summary: its id and title (data). */
const headingOf = (summary: AckSummaryData) => (summary.title ? `${summary.requisition_id} · ${summary.title}` : summary.requisition_id);

export const AckWidget: HomeWidgetView = ({ widgetKey, data }) => {
  const summaries = ackSummaries(data);
  if (summaries.length === 0) return null;
  const workbench = data["workbench_route"];
  return (
    <Widget id={widgetKey} kind="sections" roles={["coordinator"]} title={ACKSUM_LABELS.panelAcknowledgmentSummary}>
      <Stack gap={16}>
        {summaries.map((summary) => (
          <Stack gap={8} key={summary.requisition_id}>
            <AckSummary title={headingOf(summary)} rows={ackRows(summary)} />
            {summary.route ? (
              <TextLink href={summary.route} after="right" hs={hsKey("dash.ack.packets", summary.requisition_id)}>
                {WORKBENCH_LABELS.kickoffPackets}
              </TextLink>
            ) : null}
          </Stack>
        ))}
        {isAppRoute(workbench) ? (
          <TextLink href={workbench} after="right" >
            {NAV_ITEM_LABELS.coordinatorWorkbench}
          </TextLink>
        ) : null}
      </Stack>
    </Widget>
  );
};
